import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { hasAnyPermission, hasAllPermissions, type UserRole, type UserPermission } from '@/types/auth';

interface ProtectedRouteProps {
  /** The content to render if access is granted */
  children: ReactNode;
  /** Required permission(s) to access this route */
  requiredPermission?: UserPermission | UserPermission[];
  /** Required role(s) to access this route */
  requiredRole?: UserRole | UserRole[];
  /** If true, user needs ANY of the permissions/roles. If false, user needs ALL of them */
  requireAny?: boolean;
  /** Path to redirect to if access is denied. Defaults to '/unauthorized' */
  redirectTo?: string;
  /** If true, redirects to login instead of unauthorized page when not authenticated */
  redirectToLogin?: boolean;
}

/**
 * ProtectedRoute component to guard routes based on permissions or roles
 *
 * @example
 * ```tsx
 * // Protect a route that requires a specific permission
 * <Route path="/incidents/create" element={
 *   <ProtectedRoute requiredPermission="incidents:create">
 *     <CreateIncidentPage />
 *   </ProtectedRoute>
 * } />
 *
 * // Protect a route that requires a specific role
 * <Route path="/admin" element={
 *   <ProtectedRoute requiredRole="ADMIN">
 *     <AdminPanel />
 *   </ProtectedRoute>
 * } />
 *
 * // Protect a route that requires ANY of multiple permissions
 * <Route path="/incidents" element={
 *   <ProtectedRoute
 *     requiredPermission={['incidents:read', 'incidents:create']}
 *     requireAny={true}
 *   >
 *     <IncidentsPage />
 *   </ProtectedRoute>
 * } />
 *
 * // Protect a route that requires multiple permissions
 * <Route path="/admin/users" element={
 *   <ProtectedRoute
 *     requiredPermission={['users:read', 'users:create']}
 *   >
 *     <UserManagementPage />
 *   </ProtectedRoute>
 * } />
 * ```
 */
export function ProtectedRoute({
  children,
  requiredPermission,
  requiredRole,
  requireAny = false,
  redirectTo = '/unauthorized',
  redirectToLogin = false,
}: ProtectedRouteProps) {
  const location = useLocation();
  const { isAuthenticated, user } = useAuthStore((state) => ({
    isAuthenticated: state.isAuthenticated,
    user: state.user,
  }));

  // Not authenticated - redirect to login or unauthorized
  if (!isAuthenticated || !user) {
    const destination = redirectToLogin ? '/login' : redirectTo;
    return <Navigate to={destination} state={{ from: location }} replace />;
  }

  // Check permissions if required
  if (requiredPermission) {
    const permissions = Array.isArray(requiredPermission)
      ? requiredPermission
      : [requiredPermission];

    // Route through wildcard-aware helpers so `['*']` holders resolve correctly (L-026 consistency).
    const hasPermission = requireAny
      ? hasAnyPermission(user.permissions, permissions)
      : hasAllPermissions(user.permissions, permissions);

    if (!hasPermission) {
      return <Navigate to={redirectTo} state={{ from: location }} replace />;
    }
  }

  // Check roles if required
  if (requiredRole) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

    const hasRole = requireAny
      ? roles.some((role) => user.roles.some((userRole) => userRole.name === role))
      : roles.every((role) => user.roles.some((userRole) => userRole.name === role));

    if (!hasRole) {
      return <Navigate to={redirectTo} state={{ from: location }} replace />;
    }
  }

  // Access granted
  return <>{children}</>;
}
